import React, { ReactElement, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { Alert, Box, Button, Card, Typography } from '@mui/material'

import Loading from 'src/v3/components/loading'
import { EventQuiz } from 'src/v3/events/components/event-quiz'
import { ConfirmIcon } from '../core/icons'
import {
  fetchEvents,
  fetchQuiz,
  fetchQuizSessionQuestions,
  fetchAccreditations,
  fetchEventAccreditations,
  selectEventById,
  selectEventsStatus,
  selectSessionQuestionsByQuiz,
  selectSessionQuestionsStatusByQuiz,
  selectQuizByEvent,
  selectQuizStatusByEvent,
  fetchQuizSessionResult,
  selectQuizSessionResults,
  selectQuizSessionResultsStatus,
  selectAccreditationsByTypeByEvent,
} from './events.slice'

let TakeQuiz = (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { eventId } = useParams()
  let eventID = eventId || ''

  let event = useAppSelector(selectEventById(eventID))
  let eventsStatus = useAppSelector(selectEventsStatus)
  let quiz = useAppSelector(selectQuizByEvent(eventID))
  let quizStatus = useAppSelector(selectQuizStatusByEvent(eventID))
  let quizID = quiz?.id || ''
  let questions = useAppSelector(selectSessionQuestionsByQuiz(quizID))
  let questionsStatus = useAppSelector(
    selectSessionQuestionsStatusByQuiz(quizID),
  )
  let sessionResult = useAppSelector(selectQuizSessionResults(eventID))
  let sessionResultStatus = useAppSelector(
    selectQuizSessionResultsStatus(eventID),
  )
  let accreditationsByType = useAppSelector(
    selectAccreditationsByTypeByEvent(eventID),
  )

  let hasHours = (event?.hours || 0) > 0
  let isNotAccreditedLinkedService = () => {
    if (!event?.linkedServiceID) {
      return false
    }
    return (accreditationsByType?.notAccredited || []).length > 0
  }
  let hasSurvey = !isNotAccreditedLinkedService() && hasHours

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if ((eventID && !quizStatus) || quizStatus === 'uninitialized') {
      dispatch(fetchQuiz(eventID))
    }
  }, [quizStatus, eventID])

  useEffect(() => {
    if (quizID && (!questionsStatus || questionsStatus === 'uninitialized')) {
      dispatch(fetchQuizSessionQuestions(quizID))
    }
  }, [questionsStatus, quizID])

  useEffect(() => {
    if (
      eventID &&
      (!sessionResultStatus || sessionResultStatus === 'uninitialized')
    ) {
      dispatch(fetchQuizSessionResult(eventID))
    }
  }, [sessionResultStatus, eventID])

  useEffect(() => {
    if (event?.linkedServiceID) {
      dispatch(fetchAccreditations())
      dispatch(fetchEventAccreditations())
    }
  }, [event?.linkedServiceID])

  return (
    <>
      {/* <Typography variant="h5" sx={{ mb: 3 }}>
        Event Quiz
      </Typography> */}
      <Box>
        <EventQuiz
          name={event?.name || 'Event'}
          quiz={quiz}
          questionData={questions}
          apiPath="/api/event"
          proceedButton={
            <Card variant="outlined" sx={{ p: 3, mt: 2 }}>
              {hasSurvey && (
                <Alert
                  severity="error"
                  variant="outlined"
                  sx={{
                    mb: 3,
                    '& .MuiAlert-message': {
                      width: '100%',
                    },
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Important: Do not leave this page!
                  </Typography>
                  <Typography variant="body2">
                    If this course is accredited, you will only receive CE
                    credit by continuing to the survey. Leaving this page before
                    completing the survey will result in loss of credit.
                  </Typography>
                </Alert>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ textAlign: 'center', fontWeight: 500 }}
                >
                  Ready to Complete Your Event?
                </Typography>
                <Typography
                  color="textSecondary"
                  sx={{ textAlign: 'center', maxWidth: 500 }}
                >
                  Click the button below to finalize your event completion
                  {hasSurvey
                    ? ' and proceed to the required survey.'
                    : ' and access your certificate.'}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ConfirmIcon />}
                  onClick={() => {
                    dispatch(fetchQuizSessionResult(eventID))
                    navigate(`/events/${eventID}/certificate`)
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 500,
                  }}
                >
                  Complete Event
                </Button>
              </Box>
            </Card>
          }
          existingResult={sessionResult}
        />
      </Box>
      <Loading entities={[eventsStatus, quizStatus, questionsStatus]} />
    </>
  )
}

export default TakeQuiz
